import { FormInstance } from 'antd';
import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Widget } from 'api/dashboard';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import {
  BackgroundColor,
  Height,
  InputBorder,
  InputFocus,
} from 'components/atoms/input/input';
import { dateFilterOperationOptions } from 'components/organisms/dashboard/components/utils';
import { Fields, Select } from 'components/organisms/tasks/criteria/components';
import {
  FieldType,
  FilterableField,
  GroupedOptions,
} from 'components/organisms/tasks/fields';
import { Box } from 'components/organisms/tasks/filter-box/components';
import { FilterType } from 'components/organisms/tasks/filter-box/filter-type';
import { AdvancedGroupedFilterComponent } from 'components/organisms/tasks/filter-box/grouped-advanced';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { useTranslation } from 'hooks/use-translation';

const FilterWrapper = styled(Box)`
  ${Fields} {
    ${BackgroundColor};
    ${InputBorder};
    ${Height};
    padding-left: 0;
    &.ant-select-open {
      ${InputFocus};
      .ant-select-selection-item {
        color: var(--gray-100);
      }
    }
    .ant-select-selector {
      ${Height};
      display: flex;
      align-items: center;
      padding-left: var(--spacing-05);
    }
  }
  ${Select} {
    padding-left: 0;
    .ant-select-selector {
      padding-left: var(--spacing-05);
    }
  }
`;

export type FormValues = Pick<Widget, 'widgetName'>;
interface StepTwoProps {
  form: FormInstance<FormValues>;
  onFinish: (values: FormValues) => void;
  fields: GroupedOptions<FilterableField>[];
  filter: GroupedFilter;
  setFilter: React.Dispatch<React.SetStateAction<GroupedFilter>>;
  initialValues: FormValues;
}

export const StepTwo: FC<StepTwoProps> = ({
  form,
  onFinish,
  fields,
  filter,
  setFilter,
  initialValues,
}) => {
  const errorMessage = useTranslation('input.required');
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef?.current?.focus();
    }
  }, []);

  return (
    <Form
      form={form}
      colon={false}
      layout="vertical"
      initialValues={initialValues}
      onFinish={(values) => onFinish(values as FormValues)}
    >
      <Form.Item
        name="widgetName"
        label={<FormattedMessage id="name" />}
        required
        rules={[
          {
            required: true,
            whitespace: true,
            message: errorMessage,
          },
        ]}
      >
        <Input ref={inputRef} />
      </Form.Item>
      <FilterType
        readonly
        value={filter.type}
        onChange={(type) => setFilter((prev) => ({ ...prev, type }))}
      />
      <FilterWrapper>
        <AdvancedGroupedFilterComponent
          fields={fields}
          filter={filter}
          onChange={(latest) => setFilter(latest)}
          getFilterOperationTypeOptions={{
            [FieldType.Date]: dateFilterOperationOptions,
          }}
        />
      </FilterWrapper>
    </Form>
  );
};
